export class ApiError extends Error {
  constructor(message: string, public status: number, public code: string) {
    super(message);
  }
}

/** Calls the hub's API. Changes carry the header the server requires, and a failure becomes an ApiError with a sentence a person can read. */
export async function api<T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(method !== 'GET' ? { 'Content-Type': 'application/json', 'X-VXH': '1' } : {}) },
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });
  const text = await res.text();
  const data = text ? safeParse(text) : null;
  if (!res.ok) {
    const d = (data ?? {}) as { message?: string; error?: string };
    throw new ApiError(d.message ?? `Something went wrong (${res.status}).`, res.status, d.error ?? 'error');
  }
  return data as T;
}

function safeParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

/** Sends one file as the whole body of the request (the Knowledge base's uploads). The details go in the address, built by the caller. */
export async function apiUpload<T>(path: string, file: File, method: 'POST' | 'PUT'): Promise<T> {
  const res = await fetch(`/api${path}`, { method, credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/octet-stream', 'X-VXH': '1' }, body: file });
  const text = await res.text();
  const data = text ? safeParse(text) : null;
  if (!res.ok) {
    const d = (data ?? {}) as { message?: string; error?: string };
    throw new ApiError(d.message ?? (res.status === 413 ? 'The file was refused as too large. Up to 25 MB is allowed; if yours is smaller, the web proxy in front of the hub needs a larger limit (see the administrator guide).' : `Something went wrong (${res.status}).`), res.status, d.error ?? 'error');
  }
  return data as T;
}

/** Fetches a file and saves it under the given name. A refusal (not allowed, too many downloads) comes back as an ApiError with a readable sentence. */
export async function apiDownload(path: string, saveAs: string): Promise<void> {
  const res = await fetch(`/api${path}`, { credentials: 'same-origin' });
  if (!res.ok) {
    const d = (safeParse(await res.text()) ?? {}) as { message?: string; error?: string };
    throw new ApiError(d.message ?? `The download did not work (${res.status}).`, res.status, d.error ?? 'error');
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = saveAs;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
